import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import ROUTES from "../../routes";
import api from "../../config/axiosInstance";
import { showToast } from "../../utils/toast";
import { handleErrors } from "../../utils/common";

function AddProject() {
    const formRef = useRef(null);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        location: "",
        isFeatured: false,
        isCompleted: false,

        hero: {
            description: "",
            buttonLink: "",
        },

        imgBox: {
            title: "",
            description: "",
            buttonText: "",
            buttonLink: "",
            officeSpace: "",
            retailSpace: "",
            area: "",
        },
    });

    const [imageSrc, setImageSrc] = useState(null);
    const [imageSrcPreview, setImageSrcPreview] = useState(null);

    const [featuredImg, setFeaturedImg] = useState(null);
    const [featuredImgPreview, setFeaturedImgPreview] = useState(null);

    const [heroImage, setHeroImage] = useState(null);
    const [heroImagePreview, setHeroImagePreview] = useState(null);

    const [imgBoxImage, setImgBoxImage] = useState(null);
    const [imgBoxImagePreview, setImgBoxImagePreview] = useState(null);

    const [heroButtonFile, setHeroButtonFile] = useState(null);
    const [imgBoxButtonFile, setImgBoxButtonFile] = useState(null);

    const [projectStatistics, setProjectStatistics] = useState([
        { key: "", value: "" }
    ]);

    const [gallery, setGallery] = useState([
        {
            image: null,
            preview: null,
            type: "interior",
            include_dynamic_page: false,
        },
    ]);

    const [mobileGallery, setMobileGallery] = useState([
        {
            image: null,
            preview: null,
            type: "interior",
            include_dynamic_page: false,
        },
    ]);

    const [submitting, setSubmitting] = useState(false);


    useEffect(() => {

        $.validator.addMethod('extension', function (value, element, allowedExtensions) {
            if (!value) return true;
            const extension = value.split('.').pop().toLowerCase();
            return $.inArray(extension, allowedExtensions) !== -1;
        }, 'Invalid file type');

        if (formRef.current && window.$.fn.validate) {
            window.$(formRef.current).validate({
                ignore: ":hidden:not(.validate),.ck-editor *",
                rules: {
                    title: { required: true, minlength: 2 },
                    location: { required: true },
                    description: { required: true, minlength: 3 },
                    hero_description: { required: true, minlength: 3 },
                    hero_buttonLink: {
                        required: true,
                        extension: ['pdf', 'docx']
                    },
                    imgBox_title: { required: true },
                    imgBox_description: { required: true, minlength: 3 },
                    imgBox_buttonText: { required: true },
                    imgBox_buttonLink: {
                        required: true,
                        extension: ['pdf', 'docx']
                    },
                    imgBox_officeSpace: { required: true },
                    imgBox_retailSpace: { required: true },
                    imgBox_area: { required: true },
                    featuredImg: {
                        required: true,
                        extension: ['jpg', 'jpeg', 'png', 'pdf', 'svg', 'webp']
                    },
                    imageSrc: {
                        required: true,
                        extension: ['jpg', 'jpeg', 'png', 'pdf', 'svg', 'webp']
                    },
                    hero_image: {
                        required: true,
                        extension: ['jpg', 'jpeg', 'png', 'pdf', 'svg', 'webp']
                    },
                    imgBox_imgSrc: {
                        required: true,
                        extension: ['jpg', 'jpeg', 'png', 'pdf', 'svg', 'webp']
                    },
                },
                messages: {
                    title: {
                        required: "Project title is required",
                        minlength: "Title must be at least 2 characters long"
                    },
                    location: {
                        required: "Location is required"
                    },
                    description: {
                        required: "Description is required",
                        minlength: "Description must be at least 3 characters"
                    },
                    hero_description: {
                        required: "Hero description is required",
                        minlength: "Hero description must be at least 10 characters"
                    },
                    imgBox_title: {
                        required: "Image box title is required"
                    },
                    imgBox_description: {
                        required: "Image box description is required",
                        minlength: "Image box description must be at least 10 characters"
                    },
                    imgBox_buttonText: {
                        required: "Image box button text is required"
                    },
                    hero_buttonLink: {
                        required: "Hero button file is required",
                        extension: "Only PDF or DOCX files are allowed"
                    },
                    imgBox_buttonLink: {
                        required: "ImgBox button file is required",
                        extension: "Only PDF or DOCX files are allowed"
                    },
                    imgBox_officeSpace: {
                        required: "Office space is required"
                    },
                    imgBox_retailSpace: {
                        required: "Retail space is required"
                    },
                    imgBox_area: {
                        required: "Area is required"
                    },
                    imageSrc: {
                        required: "Main project image is required",
                        extension: "Allowed formats: jpg, jpeg, png, pdf, svg"
                    },
                    hero_image: {
                        required: "Hero image is required",
                        extension: "Allowed formats: jpg, jpeg, png, pdf, svg"
                    },
                    imgBox_imgSrc: {
                        required: "Boxes image is required",
                        extension: "Allowed formats: jpg, jpeg, png, pdf, svg"
                    }
                },
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, []);

    useEffect(() => {
        return () => {
            featuredImgPreview && URL.revokeObjectURL(featuredImgPreview);
            imageSrcPreview && URL.revokeObjectURL(imageSrcPreview);
            heroImagePreview && URL.revokeObjectURL(heroImagePreview);
            imgBoxImagePreview && URL.revokeObjectURL(imgBoxImagePreview);
        };
    }, [featuredImgPreview, imageSrcPreview, heroImagePreview, imgBoxImagePreview]);

    /*
    const toggleCheckbox = (e) => {
        let { name, value } = e.target;        
        value === 'false'? value = true: value = false;
        console.log(value)

        setFormData(prev => ({ ...prev, [name]: value }));
    };
    */

    const toggleCheckbox = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const handleSingleImageChange = (e, setFile, setPreview) => {
        const file = e.target.files[0];
        if (file) {
            setFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleGalleryImageChange = (index, file) => {
        const updated = [...gallery];
        updated[index].image = file;
        updated[index].preview = URL.createObjectURL(file);
        setGallery(updated);
    };

    const handleMobileGalleryImageChange = (index, file) => {
        const updated = [...mobileGallery];
        updated[index].image = file;
        updated[index].preview = URL.createObjectURL(file);
        setMobileGallery(updated);
    };

    const handleStatisticChange = (index, field, value) => {
        const updated = [...projectStatistics];
        updated[index][field] = value;
        setProjectStatistics(updated);
    };

    const addStatistic = () => {
        setProjectStatistics(prev => [...prev, { key: "", value: "" }]);
    };

    const removeStatistic = (index) => {
        setProjectStatistics(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        const $ = window.jQuery;
        if (!$(formRef.current).valid()) return;

        if (gallery.length === 0 || gallery.some(g => !g.image)) {
            showToast("error", "At least one gallery image is required");
            return;
        }

        const data = new FormData();

        data.append("title", formData.title);
        data.append("description", formData.description);
        data.append("location", formData.location);
        data.append("isFeatured", String(formData.isFeatured));
        data.append("isCompleted", String(formData.isCompleted));

        data.append("featuredImg", featuredImg);
        data.append("imageSrc", imageSrc);
        data.append("heroImage", heroImage);
        data.append("heroButtonLink", heroButtonFile);
        data.append("imgBoxImage", imgBoxImage);
        data.append("imgBoxButtonLink", imgBoxButtonFile);

        gallery.forEach(item => {
            data.append("projectGalleryImages", item.image);
        });

        mobileGallery.forEach(item => {
            data.append("mobileGalleryImages", item.image);
        });

        data.append(
            "hero",
            JSON.stringify({
                description: formData.hero.description
            })
        );

        data.append(
            "imgBox",
            JSON.stringify({
                title: formData.imgBox.title,
                description: formData.imgBox.description,
                buttonText: formData.imgBox.buttonText,
                officeSpace: formData.imgBox.officeSpace,
                retailSpace: formData.imgBox.retailSpace,
                area: formData.imgBox.area
            })
        );

        data.append(
            "projectGallery",
            JSON.stringify(
                gallery.map(item => ({
                    type: item.type,
                    include_dynamic_page: item.include_dynamic_page
                }))
            )
        );

        if (mobileGallery.length > 0) {
            data.append(
                "mobileGallery",
                JSON.stringify(
                    mobileGallery.map(item => ({
                        type: item.type,
                        include_dynamic_page: item.include_dynamic_page
                    }))
                )
            );
        }

        data.append(
            "projectStatistics",
            JSON.stringify(
                projectStatistics.filter(
                    item => item.key.trim() && item.value.trim()
                )
            )
        );

        try {
            setSubmitting(true);
            const res = await api.post("/projects/create", data, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            showToast("success", res.data.message || "Project created");
            navigate(ROUTES.PROJECTS);
        } catch (err) {
            handleErrors(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.PROJECTS}>Projects</Link>
                    </li>
                    <li className="breadcrumb-item active">Add Project</li>
                </ol>
            </div>

            <form ref={formRef} noValidate>
                <div className="card">
                    <div className="card-header">
                        <h3>BASIC</h3>
                    </div>
                    <div className="card-body">
                        <div className="row">

                            {/* BASIC */}
                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="title" className="form-label">Title</label>
                                <input
                                    className="form-control mb-2"
                                    id="title"
                                    name="title"
                                    placeholder="Title"
                                    onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="location" className="form-label">Location</label>
                                <input
                                    className="form-control mb-2"
                                    id="location"
                                    name="location"
                                    placeholder="Location"
                                    onChange={e => setFormData(p => ({ ...p, location: e.target.value }))}
                                />
                            </div>

                            {[
                                { name: 'description', label: 'Description' }
                            ].map(({ name, label }) => (
                                <div key={name} className="mb-3 col-lg-12 col-md-12 col-sm-12">
                                    <label className="form-label">{label}</label>


                                    <CKEditor
                                        editor={ClassicEditor}
                                        config={{
                                            toolbar: [
                                                'heading', '|',
                                                'bold', 'italic', 'link', 'code', 'codeBlock',
                                                '|', 'bulletedList', 'numberedList'
                                            ]
                                        }}
                                        data={formData.description}
                                        onChange={(event, editor) => {
                                            const html = editor.getData();
                                            const plainText = html.replace(/<[^>]*>/g, '').trim();

                                            setFormData(p => ({
                                                ...p,
                                                description: html
                                            }));

                                            const input = document.querySelector('input[name="description"]');
                                            input.value = plainText;

                                            $(input).valid();
                                        }}
                                    />

                                    <input
                                        type="hidden"
                                        name="description"
                                        className="validate"
                                        value={formData.description}
                                    />
                                </div>
                            ))}

                            <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                <label className="form-label">Featured Image</label>
                                <input
                                    type="file"
                                    id="featuredImg"
                                    name="featuredImg"
                                    className="form-control"
                                    onChange={(e) =>
                                        handleSingleImageChange(e, setFeaturedImg, setFeaturedImgPreview)
                                    }
                                />
                                {featuredImgPreview && (
                                    <img
                                        src={featuredImgPreview}
                                        alt="Featured Preview"
                                        style={{ width: 100, height: 100, objectFit: "cover", marginTop: 10 }}
                                    />
                                )}
                            </div>

                            <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                <label className="form-label">Main Image</label>
                                <input
                                    type="file"
                                    id="imageSrc"
                                    name="imageSrc"
                                    className="form-control"
                                    onChange={(e) =>
                                        handleSingleImageChange(e, setImageSrc, setImageSrcPreview)
                                    }
                                />
                                {imageSrcPreview && (
                                    <img
                                        src={imageSrcPreview}
                                        alt="Main Preview"
                                        style={{ width: 100, height: 100, objectFit: "cover", marginTop: 10 }}
                                    />
                                )}
                            </div>

                            <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                <div className="form-check custom-checkbox mb-3 checkbox-success">
                                    <input
                                        type="checkbox"
                                        id="isFeatured"
                                        name="isFeatured"
                                        className="form-check-input me-2"
                                        checked={formData.isFeatured}
                                        value={formData.isFeatured}
                                        onChange={toggleCheckbox}
                                    />
                                    <label className="form-check-label" htmlFor="isFeatured">Is Featured</label>
                                </div>
                            </div>

                            <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                <div className="form-check custom-checkbox mb-3 checkbox-success">
                                    <input
                                        type="checkbox"
                                        id="isCompleted"
                                        name="isCompleted"
                                        className="form-check-input me-2"
                                        checked={formData.isCompleted}
                                        value={formData.isCompleted}
                                        onChange={toggleCheckbox}
                                    />
                                    <label className="form-check-label" htmlFor="isCompleted">Is Completed</label>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h3>HERO</h3>
                    </div>
                    <div className="card-body">
                        <div className="row">

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="hero_buttonLink" className="form-label">
                                    Hero Button File (PDF/DOCX)
                                </label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="hero_buttonLink"
                                    name="hero_buttonLink"
                                    accept=".pdf,.docx"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        setHeroButtonFile(file || null);
                                    }}
                                />
                            </div>


                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label className="form-label">Image</label>
                                <input
                                    type="file"
                                    id="hero_image"
                                    name="hero_image"
                                    className="form-control"
                                    onChange={(e) =>
                                        handleSingleImageChange(e, setHeroImage, setHeroImagePreview)
                                    }
                                />
                                {heroImagePreview && (
                                    <img
                                        src={heroImagePreview}
                                        alt="Hero Preview"
                                        style={{ width: 100, height: 100, objectFit: "cover", marginTop: 10 }}
                                    />
                                )}
                            </div>

                            <div className="mb-3 col-lg-12 col-md-12 col-sm-12">
                                <label className="form-label">Description</label>
                                <CKEditor
                                    editor={ClassicEditor}
                                    config={{
                                        toolbar: [
                                            'heading', '|',
                                            'bold', 'italic', 'link', 'code', 'codeBlock',
                                            '|', 'bulletedList', 'numberedList'
                                        ]
                                    }}
                                    data={formData.hero.description}
                                    onChange={(event, editor) => {
                                        const html = editor.getData();
                                        const plainText = html.replace(/<[^>]*>/g, '').trim();

                                        setFormData(p => ({
                                            ...p,
                                            hero: { ...p.hero, description: html }
                                        }));

                                        const input = document.querySelector('input[name="hero_description"]');
                                        input.value = plainText;
                                        $(input).valid();
                                    }}
                                />
                                <input
                                    type="hidden"
                                    name="hero_description"
                                    className="validate"
                                />
                            </div>

                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h3>IMG BOX</h3>
                    </div>
                    <div className="card-body">
                        <div className="row">

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_title" className="form-label">Title</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_title"
                                    name="imgBox_title"
                                    placeholder="Title"
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, title: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_area" className="form-label">Area</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_area"
                                    name="imgBox_area"
                                    placeholder="Area"
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, area: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-12 col-md-12 col-sm-12">
                                <label className="form-label">Description</label>
                                <CKEditor
                                    editor={ClassicEditor}
                                    config={{
                                        toolbar: [
                                            'heading', '|',
                                            'bold', 'italic', 'link', 'code', 'codeBlock',
                                            '|', 'bulletedList', 'numberedList'
                                        ]
                                    }}
                                    data={formData.imgBox.description}
                                    onChange={(event, editor) => {
                                        const html = editor.getData();
                                        const plainText = html.replace(/<[^>]*>/g, '').trim();

                                        setFormData(p => ({
                                            ...p,
                                            imgBox: { ...p.imgBox, description: html }
                                        }));

                                        const input = document.querySelector('input[name="imgBox_description"]');
                                        input.value = plainText;
                                        $(input).valid();
                                    }}
                                />
                                <input
                                    type="hidden"
                                    name="imgBox_description"
                                    className="validate"
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_buttonText" className="form-label">Button Text</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_buttonText"
                                    name="imgBox_buttonText"
                                    placeholder="Button Text"
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, buttonText: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_buttonLink" className="form-label">
                                    ImgBox Button File (PDF/DOCX)
                                </label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="imgBox_buttonLink"
                                    name="imgBox_buttonLink"
                                    accept=".pdf,.docx"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        setImgBoxButtonFile(file || null);
                                    }}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_officeSpace" className="form-label">Office Space</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_officeSpace"
                                    name="imgBox_officeSpace"
                                    placeholder="Office Space"
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, officeSpace: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_retailSpace" className="form-label">Retail Space</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_retailSpace"
                                    name="imgBox_retailSpace"
                                    placeholder="Office Space"
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, retailSpace: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_imgSrc" className="form-label">Image Box Image</label>
                                <input
                                    type="file"
                                    id="imgBox_imgSrc"
                                    name="imgBox_imgSrc"
                                    className="form-control"
                                    onChange={(e) =>
                                        handleSingleImageChange(e, setImgBoxImage, setImgBoxImagePreview)
                                    }
                                />
                                {imgBoxImagePreview && (
                                    <img
                                        src={imgBoxImagePreview}
                                        alt="ImgBox Preview"
                                        style={{ width: 100, height: 100, objectFit: "cover", marginTop: 10 }}
                                    />
                                )}
                            </div>

                            {/* GALLERY */}
                            <h5>Project Gallery</h5>
                            {gallery.map((item, index) => (
                                <div key={index} className="row">
                                    <div className="mb-3 col-lg-4 col-md-4 col-sm-4">
                                        <input
                                            type="file"
                                            className="form-control"
                                            onChange={(e) =>
                                                handleGalleryImageChange(index, e.target.files[0])
                                            }
                                        />
                                        {item.preview && (
                                            <img
                                                src={item.preview}
                                                alt={`Gallery-${index}`}
                                                style={{
                                                    width: 100,
                                                    height: 100,
                                                    objectFit: "cover",
                                                    marginTop: 8
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                        <select
                                            className="form-select"
                                            value={item.type}
                                            onChange={(e) => {
                                                const updated = [...gallery];
                                                updated[index].type = e.target.value;
                                                setGallery(updated);
                                            }}
                                        >
                                            <option value="interior">Interior</option>
                                            <option value="exterior">Exterior</option>
                                        </select>
                                    </div>
                                    <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={item.include_dynamic_page}
                                                onChange={(e) => {
                                                    const updated = [...gallery];
                                                    updated[index].include_dynamic_page = e.target.checked;
                                                    setGallery(updated);
                                                }}
                                            />
                                            <label className="form-check-label">
                                                Include Dynamic Page
                                            </label>
                                        </div>
                                    </div>
                                    <div className="mb-3 col-lg-2 col-md-2 col-sm-2">
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={() =>
                                                setGallery(gallery.filter((_, i) => i !== index))
                                            }
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-primary mb-3"
                                onClick={() =>
                                    setGallery([
                                        ...gallery,
                                        {
                                            image: null,
                                            preview: null,
                                            type: "interior",
                                            include_dynamic_page: false,
                                        },
                                    ])
                                }
                            >
                                + Add Image
                            </button>

                            {/* MOBILE GALLERY */}
                            <h5>Mobile Gallery</h5>
                            {mobileGallery.map((item, index) => (
                                <div key={index} className="row">
                                    <div className="mb-3 col-lg-4 col-md-4 col-sm-4">
                                        <input
                                            type="file"
                                            className="form-control"
                                            onChange={(e) =>
                                                handleMobileGalleryImageChange(index, e.target.files[0])
                                            }
                                        />
                                        {item.preview && (
                                            <img
                                                src={item.preview}
                                                alt={`Mobile-Gallery-${index}`}
                                                style={{
                                                    width: 100,
                                                    height: 100,
                                                    objectFit: "cover",
                                                    marginTop: 8
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                        <select
                                            className="form-select"
                                            value={item.type}
                                            onChange={(e) => {
                                                const updated = [...mobileGallery];
                                                updated[index].type = e.target.value;
                                                setMobileGallery(updated);
                                            }}
                                        >
                                            <option value="interior">Interior</option>
                                            <option value="exterior">Exterior</option>
                                        </select>
                                    </div>
                                    <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={item.include_dynamic_page}
                                                onChange={(e) => {
                                                    const updated = [...mobileGallery];
                                                    updated[index].include_dynamic_page = e.target.checked;
                                                    setMobileGallery(updated);
                                                }}
                                            />
                                            <label className="form-check-label">
                                                Include Dynamic Page
                                            </label>
                                        </div>
                                    </div>
                                    <div className="mb-3 col-lg-2 col-md-2 col-sm-2">
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={() =>
                                                setMobileGallery(mobileGallery.filter((_, i) => i !== index))
                                            }
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-primary mb-3"
                                onClick={() =>
                                    setMobileGallery([
                                        ...mobileGallery,
                                        {
                                            image: null,
                                            preview: null,
                                            type: "interior",
                                            include_dynamic_page: false,
                                        },
                                    ])
                                }
                            >
                                + Add Mobile Image
                            </button>

                            {/* STATISTIC */}
                            <h5>Project Statistic</h5>
                            {projectStatistics.map((item, index) => (
                                <div key={index} className="row">
                                    <div className="mb-3 col-lg-5 col-md-5 col-sm-5">
                                        <label className="form-label">Key</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. Area"
                                            value={item.key}
                                            onChange={(e) =>
                                                handleStatisticChange(index, "key", e.target.value)
                                            }
                                        />
                                    </div>

                                    <div className="mb-3 col-lg-5 col-md-5 col-sm-5">
                                        <label className="form-label">Value</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. 3500 sqft"
                                            value={item.value}
                                            onChange={(e) =>
                                                handleStatisticChange(index, "value", e.target.value)
                                            }
                                        />
                                    </div>

                                    <div className="mb-3 col-lg-2 col-md-2 col-sm-2">
                                        <button
                                            type="button"
                                            className="mt-4 btn btn-danger"
                                            onClick={() => removeStatistic(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-primary mt-2"
                                onClick={addStatistic}
                            >
                                + Add Statistic
                            </button>
                        </div>
                    </div>
                    <div className="card-footer">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="btn btn-sm btn-primary me-2"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <i className="spinner-border spinner-border-sm me-1"></i>
                                    Submit
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-floppy-disk me-1"></i>
                                    Submit
                                </>
                            )}
                        </button>
                        <Link to={ROUTES.PROJECTS} className="btn btn-sm btn-default">
                            <i className="fas fa-times-circle"></i> Cancel
                        </Link>
                    </div>
                </div>
            </form>
        </>
    );
}

export default AddProject;
